import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Stack,
    TextField,
    Divider,
    Collapse,
    Link,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    SportsSoccer as StravaIcon,
    ExpandMore,
    ExpandLess,
    Visibility,
    VisibilityOff,
    Science
} from '@mui/icons-material';
import { apiClient } from '../../../utils/apiClient';

/**
 * SignUpPage Component
 * 
 * Handles new user registration.
 * Options:
 * 1. Standard Sign Up: Select Role -> Connect with Strava
 * 2. Beta Program: Email + Custom Strava Credentials
 */
const SignUpPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    // Standard Sign Up State
    const [selectedRole, setSelectedRole] = useState<'athlete' | 'trainer' | 'both'>('athlete');

    // Beta Program State
    const [showBeta, setShowBeta] = useState(false);
    const [betaForm, setBetaForm] = useState({
        email: '',
        client_id: '',
        client_secret: '',
    });
    const [showSecret, setShowSecret] = useState(false);

    // Monitor online/offline status
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Handle auto-expand beta section from navigation state
    const location = useLocation();
    const betaSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (location.state && (location.state as any).openBeta) {
            setShowBeta(true);
            // Small timeout to allow expand animation or render to happen
            setTimeout(() => {
                betaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [location.state]);

    /**
     * Handles Standard Strava OAuth Flow
     */
    const handleStandardSignUp = async () => {
        if (isOffline) {
            setError('You are currently offline. Please connect to the internet to sign up.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Call backend to get Strava authorization URL with selected role
            const response = await apiClient.get<{
                authorization_url: string;
            }>(`auth/strava/authorize?role=${selectedRole}`, {
                skipAuth: true,
                skipOfflineQueue: true,
            });

            // Redirect to Strava OAuth page
            window.location.href = response.authorization_url;
        } catch (err) {
            console.error('Failed to initiate Strava authentication:', err);
            setError('Failed to connect to Strava. Please try again.');
            setIsLoading(false);
        }
    };

    /**
     * Handles Beta Program Registration
     */
    const handleBetaRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isOffline) {
            setError('You are currently offline.');
            return;
        }

        if (!betaForm.email || !betaForm.client_id || !betaForm.client_secret) {
            setError('Please fill in all fields for Beta Registration.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Register beta user and get custom auth URL
            const response = await apiClient.post<{
                authorization_url: string;
                state: string;
            }>('/auth/beta-register', betaForm, {
                skipAuth: true,
                skipOfflineQueue: true,
            });

            // Redirect to the custom Strava Application Authorization URL
            window.location.href = response.authorization_url;
        } catch (err) {
            console.error('Beta registration failed:', err);
            setError('Failed to register for Beta Program. Please check your credentials and try again.');
            setIsLoading(false);
        }
    };

    const handleBetaChange = (prop: keyof typeof betaForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setBetaForm({ ...betaForm, [prop]: event.target.value });
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 2,
                py: 4,
            }}
        >
            <Box sx={{ maxWidth: 500, width: '100%' }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    {/* Offline Banner */}
                    {isOffline && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            You are currently offline. Sign up requires an internet connection.
                        </Alert>
                    )}

                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <StravaIcon sx={{ fontSize: 56, fill: '#FC4C02', mb: 1 }} />
                        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                            Join Surge
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Create an account to start your journey
                        </Typography>
                    </Box>

                    {/* Role Selection */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 1 }}>
                            I am a...
                        </Typography>
                        <Stack spacing={1.5}>
                            {(['athlete', 'trainer', 'both'] as const).map((role) => (
                                <Paper
                                    key={role}
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: 2,
                                        borderColor: selectedRole === role ? 'primary.main' : 'divider',
                                        bgcolor: selectedRole === role ? 'action.selected' : 'transparent',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'action.hover',
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                    onClick={() => setSelectedRole(role)}
                                >
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold" textTransform="capitalize">
                                            {role}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {role === 'athlete' && 'Track activities & analyze performance'}
                                            {role === 'trainer' && 'Manage athletes & create plans'}
                                            {role === 'both' && 'Access all features'}
                                        </Typography>
                                    </Box>
                                    {selectedRole === role && (
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.2)'
                                            }}
                                        />
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    </Box>

                    {/* Connect Button */}
                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleStandardSignUp}
                        disabled={isLoading || isOffline}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <StravaIcon />}
                        sx={{
                            bgcolor: '#FC4C02',
                            color: 'white',
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            mb: 3,
                            '&:hover': {
                                bgcolor: '#E34402',
                            },
                            '&:disabled': {
                                bgcolor: 'grey.300',
                            },
                        }}
                    >
                        {isLoading ? 'Connecting...' : 'Sign Up with Strava'}
                    </Button>

                    <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            OR
                        </Typography>
                    </Divider>

                    {/* Beta Registration Section */}
                    <Box ref={betaSectionRef}>
                        <Button
                            fullWidth
                            color="secondary"
                            onClick={() => setShowBeta(!showBeta)}
                            endIcon={showBeta ? <ExpandLess /> : <ExpandMore />}
                            startIcon={<Science />}
                            sx={{ justifyContent: 'space-between', mb: 2 }}
                        >
                            Join Beta Program (BYO App)
                        </Button>

                        <Collapse in={showBeta}>
                            <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.paper', mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                    Step 1: Create your Strava Application
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    To ensure the best experience and bypass rate limits, we ask beta users to use their own Strava API credentials.
                                    Follow the <Link href="https://developers.strava.com/docs/getting-started/" target="_blank" rel="noopener">Strava Getting Started Guide</Link> to create an app.
                                </Typography>

                                <Alert severity="info" sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Use these settings when creating your app:
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2, typography: 'caption' }}>
                                        <li>
                                            <strong>Application Name:</strong> Surge
                                        </li>
                                        <li>
                                            <strong>Category:</strong> Performance Analysis
                                        </li>
                                        <li>
                                            <strong>Website:</strong>{' '}
                                            <Box component="span" sx={{ userSelect: 'all', fontFamily: 'monospace' }}>
                                                https://brunov21.github.io/Surge/
                                            </Box>
                                        </li>
                                        <li>
                                            <strong>Authorization Callback Domain:</strong>{' '}
                                            <Box component="span" sx={{ userSelect: 'all', fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                mclovinittt-kinetic-run-api.hf.space
                                            </Box>
                                        </li>
                                    </Box>
                                </Alert>

                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                    Step 2: Enter your Credentials
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
                                    Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> from your <Link href="https://www.strava.com/settings/api" target="_blank" rel="noopener">Strava API Settings</Link> page and paste them below.
                                    These are stored securely and used only to sync your activities.
                                </Typography>

                                <form onSubmit={handleBetaRegister}>
                                    <Stack spacing={2}>
                                        <TextField
                                            label="Email Address"
                                            type="email"
                                            fullWidth
                                            required
                                            size="small"
                                            value={betaForm.email}
                                            onChange={handleBetaChange('email')}
                                        />
                                        <TextField
                                            label="Client ID"
                                            fullWidth
                                            required
                                            size="small"
                                            value={betaForm.client_id}
                                            onChange={handleBetaChange('client_id')}
                                            helperText="Found in your Strava API Settings"
                                        />
                                        <TextField
                                            label="Client Secret"
                                            type={showSecret ? 'text' : 'password'}
                                            fullWidth
                                            required
                                            size="small"
                                            value={betaForm.client_secret}
                                            onChange={handleBetaChange('client_secret')}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowSecret(!showSecret)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showSecret ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Button
                                            type="submit"
                                            variant="outlined"
                                            color="secondary"
                                            disabled={isLoading || isOffline}
                                        >
                                            Register & Connect
                                        </Button>
                                    </Stack>
                                </form>
                            </Paper>
                        </Collapse>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Already have an account?{' '}
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => navigate('/login')}
                                sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                            >
                                Log In
                            </Link>
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default SignUpPage;
